/**
 * Mirrors SalesProjects.jsx: grouped quoted projects + Project requests bucket rules
 * so P&L can hide only leads that still appear in Project requests.
 */

function isTruthyRejectedFlag(value) {
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return Boolean(value);
}

/** Same shape as SalesProjects `groupedProjects` (ProjectManagerAction + quotationId). */
export function buildGroupedProjectsForProjectRequests(projects, employees) {
  if (!projects?.length) return [];

  const filteredProjects = projects.filter((proj) => proj?.isProjecttracking === false);

  const groupedByProjectName = filteredProjects.reduce((acc, project) => {
    const key = project.quotationId
      ? String(project.quotationId).trim()
      : `${project.ProjectName || ""}||${project.ClientName || ""}||${project.AssignedGroup || ""}`
          .toLowerCase()
          .trim();
    if (!acc[key]) acc[key] = [];
    acc[key].push(project);
    return acc;
  }, {});

  const getNameFromEmail = (email) => {
    if (!employees?.length) return email;
    const person = employees.find((emp) => emp.EmpEmail === email);
    return person ? `${person.EmpFirstName} ${person.EmpLastName}`.trim() || email : email;
  };

  return Object.entries(groupedByProjectName).map(([compositeKey, projectGroup]) => {
    const projectName =
      projectGroup[0]?.ProjectName || compositeKey.split("||")[0];
    const allAssignedManagerEmails = projectGroup.flatMap(
      (p) => p.AssignedManagerEmail || []
    );

    const flaggedProject = projectGroup.find(
      (p) => p.PrimaryResources === "1" || Number(p.PrimaryResources) === 1
    );

    const primaryResourceEmails = flaggedProject
      ? flaggedProject.AssignedManagerEmail || []
      : projectGroup.flatMap((p) => {
          if (
            typeof p.PrimaryResources === "string" &&
            p.PrimaryResources.includes("@")
          ) {
            return [p.PrimaryResources];
          }
          if (Array.isArray(p.PrimaryResources)) {
            return p.PrimaryResources.filter((email) => email.includes("@"));
          }
          return [];
        });

    const AssignedManager = [...new Set(allAssignedManagerEmails)].map(
      getNameFromEmail
    );
    const PrimaryResource = [...new Set(primaryResourceEmails)].map(
      getNameFromEmail
    );

    const ProjectManagerAction = projectGroup.map((p) => {
      const managerEmail = (p.AssignedManagerEmail || [])[0];
      const managerName = getNameFromEmail(managerEmail);

      let salesStatus = " ";
      if (p.Assignedperson && p.Assignedperson.length > 0) {
        const statuses = p.Assignedperson.map((person) => person.salespersonstatus);
        if (statuses.includes("response received")) {
          salesStatus = "response received";
        } else if (statuses.includes("project assigned")) {
          salesStatus = "project assigned";
        }
      }

      const AssignedPerson = p.Assignedperson
        ? p.Assignedperson.map((person) => ({
            name: person.name,
            email: person.email,
            salespersonstatus: person.salespersonstatus || " ",
            salespersonaction: person.salespersonaction || " ",
            manageraction: person.manageraction || " ",
            operationaction: person.operationaction || " ",
            taskStatus: person.taskStatus || " ",
            OpDueDate: person.OpDueDate || null,
            _id: person._id,
            isProjectoutstanding: person.isProjectoutstanding || false,
          }))
        : [];

      return {
        ProjectId: p._id,
        Manager: managerName,
        ManagerEmail: managerEmail,
        ManagerAction: p.ProjectManagerAction || " ",
        ResourceType: p.ResourceType || " ",
        TypeService: p.TypeService,
        SalesPersonStatus: salesStatus,
        AssignedPerson,
        ProjectManagerActionSummary: p.ProjectManagerAction,
        isRejectedProject:
          isTruthyRejectedFlag(p?.isRejectedProject) ||
          isTruthyRejectedFlag(p?.isRejectedproject),
      };
    });

    return {
      ProjectName: projectName,
      ClientName: projectGroup[0]?.ClientName || "",
      AssignedGroup: projectGroup[0]?.AssignedGroup || "",
      quotationId: projectGroup[0]?.quotationId || null,
      DateDue: projectGroup[0].ProjectDueDate,
      AssignedManager,
      PrimaryResource,
      ProjectManagerAction,
      isRejectedProject: projectGroup.some(
        (p) =>
          isTruthyRejectedFlag(p?.isRejectedProject) ||
          isTruthyRejectedFlag(p?.isRejectedproject)
      ),
    };
  });
}

const RESPONDED_STATUSES = new Set([
  "response received",
  "project approved",
  "project assigned",
  "project ongoing",
  "project complete",
  "project assigned to other",
]);

/** Match a lead to a project document (quotationId first, else composite key). */
function leadMatchesProjectRow(lead, proj) {
  if (lead == null || proj == null) return false;
  const leadQ =
    lead?.quotationId != null ? String(lead.quotationId).trim() : "";
  const projQ =
    proj?.quotationId != null ? String(proj.quotationId).trim() : "";
  if (leadQ !== "" && projQ !== "" && leadQ === projQ) return true;
  const leadKey = `${lead.ProjectName || ""}||${lead.ClientName || ""}||${lead.AssignedGroup || ""}`
    .toLowerCase()
    .trim();
  const projKey = `${proj.ProjectName || ""}||${proj.ClientName || ""}||${proj.AssignedGroup || ""}`
    .toLowerCase()
    .trim();
  return (
    leadKey === projKey && String(lead.ProjectName || "").trim() !== ""
  );
}

/**
 * True if any assignee has moved past "project request outstanding" workflow:
 * — response received only counts when SP action is accept;
 * — later stages (approved → complete, tracking rows) count on status alone.
 */
function projectRowShowsPastProjectRequestOutstandings(proj) {
  if (!proj?.Assignedperson?.length) return false;
  return proj.Assignedperson.some((person) => {
    if (person?.isProjectoutstanding === true) return false;
    const st = String(person.salespersonstatus || "")
      .trim()
      .toLowerCase();
    const action = String(person.salespersonaction || "")
      .trim()
      .toLowerCase();
    if (!RESPONDED_STATUSES.has(st)) return false;
    if (st === "response received") {
      return action === "accept";
    }
    return true;
  });
}

/**
 * When quoted groups are missing (e.g. work only on tracking projects), use raw projects.
 */
function leadHasPastProjectRequestOutstandingsOnProjects(lead, projects) {
  if (!Array.isArray(projects) || projects.length === 0) return false;
  return projects.some(
    (proj) =>
      leadMatchesProjectRow(lead, proj) &&
      projectRowShowsPastProjectRequestOutstandings(proj)
  );
}

function allManagersFullyResponded(gp) {
  if (!gp?.ProjectManagerAction?.length) return false;
  return gp.ProjectManagerAction.every((action) => {
    if (!action.AssignedPerson || action.AssignedPerson.length === 0) {
      return false;
    }
    return action.AssignedPerson.every((person) => {
      const st = String(person?.salespersonstatus || "")
        .trim()
        .toLowerCase();
      const outstanding = person?.isProjectoutstanding;
      // Align with projectRowShowsPastProjectRequestOutstandings: only explicit "outstanding" hides a row.
      const isOutstanding =
        outstanding === true || String(outstanding).toLowerCase() === "true";
      return RESPONDED_STATUSES.has(st) && !isOutstanding;
    });
  });
}

/**
 * True iff this lead would still appear in SalesProjects "Project requests" table.
 * @param {object[]} projects — full Redux projects list (quoted + tracking); used when quoted-only groups no longer match (e.g. ongoing/complete on tracking rows).
 */
export function isLeadStillInProjectRequestsBucket(
  lead,
  groupedProjects,
  projects
) {
  const spStatus = String(lead?.SalesPersonStatus ?? lead?.salesPersonStatus ?? "")
    .trim()
    .toLowerCase();
  if (spStatus !== "project requested") return false;

  const matchingGroupedProjects = (groupedProjects || []).filter((gp) => {
    if (lead.quotationId && gp.quotationId) {
      return String(lead.quotationId).trim() === String(gp.quotationId).trim();
    }
    const projectKey = `${lead.ProjectName || ""}||${lead.ClientName || ""}||${lead.AssignedGroup || ""}`
      .toLowerCase()
      .trim();
    const gpKey = `${gp.ProjectName || ""}||${gp.ClientName || lead.ClientName || ""}||${gp.AssignedGroup || lead.AssignedGroup || ""}`
      .toLowerCase()
      .trim();
    return gpKey === projectKey;
  });

  if (!matchingGroupedProjects.length) {
    if (leadHasPastProjectRequestOutstandingsOnProjects(lead, projects)) {
      return false;
    }
    return true;
  }

  const allResponded = matchingGroupedProjects.every((gp) =>
    allManagersFullyResponded(gp)
  );
  if (allResponded) {
    return false;
  }

  if (leadHasPastProjectRequestOutstandingsOnProjects(lead, projects)) {
    return false;
  }

  return true;
}
